<template>
  <div>
    <v-content>
      <div class="recipe-container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Cargando información de la receta...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <div class="error-message">
            <h3>Error al cargar la receta</h3>
            <p>{{ error }}</p>
            <button @click="loadRecipeData" class="retry-btn">
              Reintentar
            </button>
          </div>
        </div>

        <!-- Main Recipe View -->
        <div v-else-if="!showSubgroupDetail" class="recipe-main">
          <!-- Recipe Header -->
          <div class="recipe-header-section">
            <!-- Recipe Image Background -->
            <div v-if="recipeData.image" class="recipe-image-background">
              <img
                :src="getImageUrl(recipeData.image)"
                :alt="recipeData.name"
                class="recipe-background-image"
              />
              <div class="image-overlay"></div>
            </div>

            <!-- Recipe Title Overlay -->
            <div class="title-overlay">
              <h1 class="recipe-title">{{ recipeData.name }}</h1>
              <div class="preparation-time">
                <span class="time-icon">📝</span>
                <span>{{ recipeData.description }}</span>
              </div>
            </div>
          </div>

          <!-- Macros Summary -->
          <div class="macros-summary-section">
            <div class="macros-grid">
              <div class="macro-item">
                <span class="macro-icon">🔥</span>
                <div class="macro-content">
                  <span class="macro-value">{{
                    recipeData.macros && recipeData.macros.calories
                  }}</span>
                  <span class="macro-label">kcal</span>
                </div>
              </div>
              <div class="macro-item">
                <span class="macro-icon">💪</span>
                <div class="macro-content">
                  <span class="macro-value"
                    >{{
                      recipeData.macros && recipeData.macros.proteins
                    }}g</span
                  >
                  <span class="macro-label">Proteínas</span>
                </div>
              </div>
              <div class="macro-item">
                <span class="macro-icon">🌾</span>
                <div class="macro-content">
                  <span class="macro-value"
                    >{{
                      recipeData.macros && recipeData.macros.carbohydrates
                    }}g</span
                  >
                  <span class="macro-label">Carbohidratos</span>
                </div>
              </div>
              <div class="macro-item">
                <span class="macro-icon">🥑</span>
                <div class="macro-content">
                  <span class="macro-value"
                    >{{ recipeData.macros && recipeData.macros.fats }}g</span
                  >
                  <span class="macro-label">Grasas</span>
                </div>
              </div>
              <div class="macro-item">
                <span class="macro-icon">⚖️</span>
                <div class="macro-content">
                  <span class="macro-value">{{ getTotalWeight() }}g</span>
                  <span class="macro-label">Peso total</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Ingredients Section -->
          <div class="content-section">
            <h2 class="section-title">Ingredientes:</h2>
            <div class="ingredients-table-container">
              <table class="ingredients-table">
                <thead>
                  <tr>
                    <th>SUBGRUPOS</th>
                    <th>NOMBRE</th>
                    <th>PROT</th>
                    <th>CARB</th>
                    <th>GRASAS</th>
                    <th>COSTO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tableRows" :key="row.id">
                    <td
                      v-if="row.isFirst"
                      class="subgroup-cell"
                      :rowspan="row.rowspan"
                    >
                      <button
                        class="subgroup-link"
                        @click="viewSubgroupDetail(row.subgroup)"
                      >
                        {{ row.subgroup.name }}
                      </button>
                      <div class="subgroup-badges">
                        <span
                          class="unit-badge ml-2"
                          :class="
                            getUnitClass(row.subgroup.measurement_unit.name)
                          "
                        >
                          {{ row.subgroup.measurement_unit.name }}
                        </span>
                        <span
                          class="temp-indicator ml-2"
                          :class="getTempClass(row.subgroup.temperature)"
                        >
                          {{ row.subgroup.temperature }}
                        </span>
                        <span
                          class="unit-badge ml-2"
                          :class="getUnitClass(row.subgroup.unit_served)"
                        >
                          Unidades Servidas: {{ row.subgroup.unit_served }}
                        </span>
                      </div>
                      <button
                        @click="viewSubgroupDetail(row.subgroup)"
                        class="detail-btn mt-4"
                      >
                        Ver Detalle
                      </button>
                    </td>
                    <td class="ingredient-name">
                      {{ row.ingredient.ingredient.NOMALIM }}
                    </td>
                    <td class="protein-cell">
                      <span
                        v-if="row.ingredient.ingredient.PROCNTg !== undefined"
                      >
                        <i class="fas fa-drumstick-bite"></i>
                        {{ formatNumber(row.ingredient.ingredient.PROCNTg) }}g
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="carb-cell">
                      <span
                        v-if="row.ingredient.ingredient.CHOCDFg !== undefined"
                      >
                        <i class="fas fa-bread-slice"></i>
                        {{ formatNumber(row.ingredient.ingredient.CHOCDFg) }}g
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="fat-cell">
                      <span v-if="row.ingredient.ingredient.FATg !== undefined">
                        <i class="fas fa-bacon"></i>
                        {{ formatNumber(row.ingredient.ingredient.FATg) }}g
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="cost-cell">
                      <span class="cost-value">
                        {{ currency_symbol }}
                        {{ formatCost(row.ingredient.total_cost) }}
                      </span>
                    </td>
                  </tr>
                  <!-- Fila de totales por subgrupo -->
                  <!--        <tr
                    v-for="subgroup in subgroups"
                    :key="`total-${subgroup.id}`"
                    class="subgroup-total-row"
                  >
                    <td colspan="4" class="subgroup-total-label">
                      <strong>Total {{ subgroup.name }}:</strong>
                    </td>
                    <td class="subgroup-total-cost">
                      <strong>{{
                        formatCost(getSubgroupTotalCost(subgroup))
                      }}</strong>
                    </td>
                  </tr> -->
                  <!-- Fila de total general -->
                  <tr class="recipe-total-row">
                    <td colspan="5" class="recipe-total-label">
                      <strong>COSTO TOTAL DE LA RECETA:</strong>
                    </td>
                    <td class="recipe-total-cost">
                      <strong>{{
                        currency_symbol + " " + formatCost(getRecipeTotalCost())
                      }}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Preparation Section -->
          <div class="content-section">
            <h2 class="section-title">Instrucciones de producción:</h2>
            <div class="preparation-table-container">
              <table class="preparation-table">
                <thead>
                  <tr>
                    <th class="subgroup-col">Subgrupo</th>
                    <th class="procedure-col">Procedimiento</th>
                    <th class="weight-col">Peso cocido</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="subgroup in subgroups"
                    :key="`prep-${subgroup.id}`"
                  >
                    <td class="subgroup-cell">
                      <span class="subgroup-name">{{ subgroup.name }}</span>
                    </td>
                    <td class="procedure-cell">
                      <div class="procedure-content">
                        {{
                          subgroup.production_instructions ||
                            "Instrucciones de preparación no disponibles."
                        }}
                      </div>
                    </td>

                    <td class="weight-cell">
                      <span class="weight-value"
                        >{{ subgroup.total_cooked_weight }}g</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Additional Information Sections -->
          <div class="additional-info-section">
            <!-- Flavor Section -->
            <div class="info-block">
              <h3 class="info-title">Validación de Sabor por Subgrupo:</h3>
              <div class="info-content">
                <div
                  v-for="subgroup in subgroups"
                  :key="subgroup.id"
                  class="flavor-subgroup-block"
                >
                  <strong>{{ subgroup.name }}:</strong>
                  <span
                    v-if="
                      subgroup.flavor_validation &&
                        subgroup.flavor_validation.trim() !== ''
                    "
                  >
                    {{ subgroup.flavor_validation }}
                  </span>
                  <span v-else class="no-info">-</span>
                </div>
              </div>
            </div>

            <!-- Storage Section -->
            <div class="info-block">
              <h3 class="info-title">
                Instrucciones de almacenamiento por Subgrupo:
              </h3>
              <div class="info-content">
                <div
                  v-for="subgroup in subgroups"
                  :key="subgroup.id"
                  class="storage-subgroup-block"
                >
                  <strong>{{ subgroup.name }}:</strong>
                  <span
                    v-if="
                      subgroup.storage_instructions &&
                        subgroup.storage_instructions.trim() !== ''
                    "
                  >
                    {{ subgroup.storage_instructions }}
                  </span>
                  <span v-else class="no-info">-</span>
                </div>
              </div>
            </div>

            <!-- Recommendations Section -->
            <div class="info-block">
              <h3 class="info-title">Instrucciones:</h3>
              <div class="info-content">
                <div class="recommendations-grid">
                  <div class="recommendation-item">
                    <strong>Empaque de Delivery:</strong>
                    <p>
                      {{
                        recipeData.instructions.delivery_packing ||
                          "No especificado"
                      }}
                    </p>
                  </div>
                  <div class="recommendation-item">
                    <strong>Empaque en Sitio:</strong>
                    <p>
                      {{
                        recipeData.instructions.site_packing ||
                          "No especificado"
                      }}
                    </p>
                  </div>
                  <div class="recommendation-item">
                    <strong>Servido:</strong>
                    <p>
                      {{ recipeData.instructions.serving || "No especificado" }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Download Button -->
            <div class="download-section">
              <button class="download-btn" @click="downloadRecipePDF">
                <span class="download-icon">📄</span>
                DESCARGAR RECETA .PDF
              </button>
            </div>
          </div>
        </div>

        <!-- Subgroup Detail View (unchanged) -->
        <div v-else class="subgroup-detail">
          <div class="detail-header">
            <button @click="backToMain" class="back-btn">
              ← Volver a la receta
            </button>
            <h1>Detalle del Subgrupo: {{ selectedSubgroup.name }}</h1>
          </div>

          <!-- Ingredients Table -->
          <div class="detail-section">
            <h2>Ingredientes</h2>
            <table class="ingredients-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Peso Crudo</th>
                  <th>Peso Cocido</th>
                  <th>Calorías</th>
                  <th>Proteínas</th>
                  <th>Carbohidratos</th>
                  <th>Grasas</th>
                  <th>Costo</th>
                </tr>
              </thead>
              <tbody v-if="subgroupIngredients.length">
                <tr
                  v-for="ingredient in subgroupIngredients"
                  :key="ingredient.id"
                >
                  <td>{{ ingredient && ingredient.name }}</td>
                  <td>{{ ingredient && ingredient.raw_weight }}g</td>
                  <td>{{ ingredient && ingredient.cooked_weight }}g</td>
                  <td>{{ ingredient && ingredient.calories }} kcal</td>
                  <td>{{ ingredient && ingredient.proteins }}g</td>
                  <td>{{ ingredient && ingredient.carbs }}g</td>
                  <td>{{ ingredient && ingredient.fats }}g</td>
                  <td>
                    {{ currency_symbol }}
                    {{ formatCost(ingredient && ingredient.total_cost) }}
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="7" class="no-data">
                    No hay ingredientes disponibles
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Cost Summary -->
          <div class="detail-section">
            <h2>Resumen de Costos</h2>
            <div class="cost-summary">
              <div class="cost-item">
                <label>Costo total del subgrupo:</label>
                <span class="cost-value">{{
                  currency_symbol +
                    " " +
                    formatCost(getSubgroupTotalCost(selectedSubgroup))
                }}</span>
              </div>
            </div>
          </div>

          <!-- Unit of Measure -->
          <div class="detail-section">
            <h2>Unidad de Medida</h2>
            <div class="unit-info">
              <div class="unit-item">
                <label>Tipo de unidad seleccionada:</label>
                <span class="unit-value">{{
                  selectedSubgroup.measurement_unit.name
                }}</span>
              </div>
              <div class="unit-item">
                <label>Equivalencia:</label>
                <span class="unit-value"
                  >1 {{ selectedSubgroup.measurement_unit.name }} =
                  {{ selectedSubgroup.equivalence_grams }}g</span
                >
              </div>
              <div class="unit-item">
                <label>Unidades servidas:</label>
                <span class="unit-value">{{
                  selectedSubgroup.unit_served
                }}</span>
              </div>
            </div>
          </div>

          <!-- Technical Attributes -->
          <div class="detail-section">
            <h2>Atributos Técnicos</h2>
            <div class="attributes-grid">
              <div class="attribute-item">
                <label>Validación de sabor:</label>
                <span>{{ selectedSubgroup.flavor_validation }}</span>
              </div>
              <div class="attribute-item">
                <label>Temperatura:</label>
                <span
                  class="temp-badge"
                  :class="getTempClass(selectedSubgroup.temperature)"
                >
                  {{ selectedSubgroup.temperature }}
                </span>
              </div>
              <div class="attribute-item">
                <label>Instrucciones de producción:</label>
                <span>{{ selectedSubgroup.production_instructions }}</span>
              </div>
              <div class="attribute-item">
                <label>Hora de producción:</label>
                <span>{{ selectedSubgroup.production_time }}</span>
              </div>
              <div class="attribute-item">
                <label>Instrucciones de almacenamiento:</label>
                <span>{{ selectedSubgroup.storage_instructions }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-content>
  </div>
</template>

<script>
import BackofficeApi from "../../gateways/backoffice-api";
export default {
  name: "RecipeDetail",
  data() {
    return {
      currency_symbol: "",
      loading: true,
      error: null,
      showSubgroupDetail: false,
      selectedSubgroup: null,
      recipeData: {
        id: null,
        name: "",
        image: "",
        instructions: {
          serving: "",
          delivery_packing: "",
          site_packing: ""
        },
        macros: {
          calories: 0,
          proteins: 0,
          carbohydrates: 0,
          fats: 0
        }
      },
      subgroups: [],
      subgroupIngredients: [],
      apiBaseUrl: "/api"
    };
  },
  computed: {
    mealId() {
      return this.$route.params.id;
    },
    tableRows() {
      const rows = [];
      this.subgroups.forEach(subgroup => {
        const ingredients =
          subgroup.ingredients && subgroup.ingredients.length > 0
            ? subgroup.ingredients
            : [
                {
                  ingredient: {
                    NOMALIM: "Sin ingredientes",
                    PROCNTg: undefined,
                    CHOCDFg: undefined,
                    FATg: undefined
                  },
                  raw_weight: 0,
                  cook_method: undefined,
                  cut_id: undefined
                }
              ];
        ingredients.forEach((item, idx) => {
          rows.push({
            id: `subgroup-${subgroup.id}-ingredient-${idx}`,
            isFirst: idx === 0,
            rowspan: ingredients.length,
            subgroup,
            ingredient: item
          });
        });
      });
      return rows;
    }
  },
  mounted() {
    this.loadRecipeData();
  },
  methods: {
    async loadRecipeData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await BackofficeApi.get(
          `/plate/subgroups-global/${this.mealId}`
        );

        if (!response.data.status === 200) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.data;

        if (result.message && result.data) {
          this.recipeData = result.data.meal;
          this.currency_symbol = result.data.currency_symbol;
          // Filtrar solo subgrupos que deben mostrarse
          this.subgroups = result.data.subgroups;
        } else {
          throw new Error("Formato de respuesta inesperado");
        }
      } catch (err) {
        this.error = err.message;
        console.error("Error loading recipe data:", err);
      } finally {
        this.loading = false;
      }
    },

    async viewSubgroupDetail(subgroup) {
      this.selectedSubgroup = subgroup;
      this.showSubgroupDetail = true;

      // Usar los ingredientes reales del subgrupo
      if (subgroup.ingredients && subgroup.ingredients.length > 0) {
        this.subgroupIngredients = subgroup.ingredients.map(item => ({
          id: item.id,
          name: item.ingredient.NOMALIM,
          raw_weight: parseFloat(item.raw_weight || 0),
          cooked_weight: parseFloat(item.cooked_weight || 0),
          calories: parseFloat(item.ingredient.ENERC || 0),
          proteins: parseFloat(item.ingredient.PROCNTg || 0),
          carbs: parseFloat(item.ingredient.CHOCDFg || 0),
          fats: parseFloat(item.ingredient.FATg || 0),
          // Usar el total_cost del objeto de la relación, no del objeto ingredient global
          total_cost: parseFloat(item.total_cost || 0),
          cook_method: item.cook_method,
          porcion: item.porcion
        }));
      } else {
        this.subgroupIngredients = [];
      }
    },

    backToMain() {
      this.showSubgroupDetail = false;
      this.selectedSubgroup = null;
      this.subgroupIngredients = [];
    },

    getUnitClass(unit) {
      const unitMap = {
        "Taza 250ml": "unit-cup",
        "Vaso 200ml": "unit-glass",
        default: "unit-default"
      };
      return unitMap[unit] || unitMap.default;
    },

    getTempClass(temperature) {
      const tempMap = {
        Ambiente: "temp-ambient",
        Frío: "temp-cold",
        Caliente: "temp-hot"
      };
      return tempMap[temperature] || "temp-ambient";
    },

    getImageUrl(imageName) {
      return imageName
        ? `https://d1u6ma1fmhichy.cloudfront.net/brands/meals/${imageName}`
        : "/placeholder.svg?height=200&width=300";
    },

    getSubgroupRowspan(subgroup) {
      // Retornar el número real de ingredientes + 1 para el header
      return Math.max(
        1,
        (subgroup.ingredients && subgroup.ingredients.length) || 0
      );
    },

    getFirstIngredientName(subgroup) {
      // Obtener el nombre del primer ingrediente real
      if (subgroup.ingredients && subgroup.ingredients.length > 0) {
        return subgroup.ingredients[0].ingredient.NOMALIM;
      }
      return "Sin ingredientes";
    },

    getFirstIngredientWeight(subgroup) {
      // Obtener el peso del primer ingrediente
      if (subgroup.ingredients && subgroup.ingredients.length > 0) {
        return parseFloat(subgroup.ingredients[0].raw_weight || 0);
      }
      return 0;
    },

    getAdditionalIngredients(subgroup) {
      // Obtener ingredientes adicionales (del segundo en adelante)
      if (subgroup.ingredients && subgroup.ingredients.length > 1) {
        return subgroup.ingredients.slice(1).map(item => ({
          name: item.ingredient.NOMALIM,
          weight: parseFloat(item.raw_weight || 0),
          cook_method: item.cook_method
        }));
      }
      return [];
    },

    getTotalWeight() {
      const total = this.subgroups.reduce((sum, subgroup) => {
        return sum + parseFloat(subgroup.total_cooked_weight || 0);
      }, 0);
      return total.toFixed(4);
    },

    getFlavorDescription() {
      return this.subgroups
        .map(s => s.flavor_validation)
        .filter(Boolean)
        .join(", ");
    },

    getStorageInstructions() {
      return this.subgroups
        .map(s => s.storage_instructions)
        .filter(Boolean)
        .join(", ");
    },

    formatNumber(val) {
      if (val === undefined || val === null || isNaN(val)) return "-";
      return Number(val).toFixed(2);
    },

    formatCost(cost) {
      if (cost === undefined || cost === null || isNaN(cost) || cost === 0)
        return "-";
      return `${Number(cost).toFixed(2)}`;
    },

    getSubgroupTotalCost(subgroup) {
      if (!subgroup.ingredients) return 0;
      return subgroup.ingredients.reduce((total, ingredient) => {
        return total + (ingredient.total_cost || 0);
      }, 0);
    },

    getRecipeTotalCost() {
      return this.subgroups.reduce((total, subgroup) => {
        return total + this.getSubgroupTotalCost(subgroup);
      }, 0);
    },

    async downloadRecipePDF() {
      try {
        const response = await BackofficeApi.get(
          `/recipes-download-pdf/${this.mealId}`,
          {
            responseType: "blob"
          }
        );

        // Crear un objeto URL para el blob
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Crear un enlace temporal y hacer clic en él
        const link = document.createElement("a");
        link.href = url;
        link.download = `receta-${this.recipeData.name || this.mealId}.pdf`;
        document.body.appendChild(link);
        link.click();

        // Limpiar
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al descargar el PDF:", error);
        // Aquí podrías mostrar un mensaje de error al usuario
      }
    }
  }
};
</script>

<style scoped>
.recipe-container {
  padding: 20px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #fafafa;
}

/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  text-align: center;
  color: #d32f2f;
}

.retry-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

/* Recipe Header */
.recipe-header-section {
  background: white;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  height: 300px;
  overflow: hidden;
}

.recipe-image-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.recipe-background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2),
    rgba(0, 0, 0, 0.7)
  );
}

.title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  padding: 30px;
}

.recipe-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0 0 15px 0;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.preparation-time {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.time-icon {
  font-size: 1.2rem;
}

/* Macros Summary */
.macros-summary-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.macros-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
}

.macro-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.macro-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.macro-icon {
  font-size: 1.5rem;
}

.macro-content {
  display: flex;
  flex-direction: column;
}

.macro-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
}

.macro-label {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

/* Content Sections */
.content-section {
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px 0;
  border-bottom: 3px solid #4caf50;
  padding-bottom: 10px;
}

/* Tables */
.ingredients-table-container,
.preparation-table-container {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.ingredients-table,
.preparation-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.ingredients-table th,
.ingredients-table td,
.preparation-table th,
.preparation-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.ingredients-table th,
.preparation-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #4a5568;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subgroup-col {
  width: 150px;
}

.name-col {
  width: 40%;
}

.weight-col {
  width: 100px;
}

.actions-col {
  width: 120px;
}

.procedure-col {
  width: 50%;
}

.variation-col {
  width: 100px;
}

.subgroup-cell {
  background: #f8fafc;
  border-right: 3px solid #4caf50;
}

.subgroup-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subgroup-name {
  font-weight: 700;
  color: #2d3748;
  font-size: 0.95rem;
}

.subgroup-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.unit-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.unit-cup {
  background: #4299e1;
}
.unit-glass {
  background: #38b2ac;
}
.unit-default {
  background: #718096;
}

.temp-indicator {
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
}

.temp-ambient {
  background: #fed7d7;
  color: #c53030;
}
.temp-cold {
  background: #bee3f8;
  color: #2b6cb0;
}
.temp-hot {
  background: #fbb6ce;
  color: #b83280;
}

.ingredient-name {
  font-weight: 500;
  color: #4a5568;
}

.weight-cell {
  font-weight: 600;
  color: #2d3748;
  text-align: center;
}

.detail-btn {
  background: #667eea;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.detail-btn:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

.procedure-cell {
  line-height: 1.5;
  color: #4a5568;
}

.procedure-content {
  max-width: 500px;
}

.variation-cell {
  text-align: center;
}

.variation-percentage {
  font-weight: 700;
  color: #2d3748;
  background: #e6fffa;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Additional Info Section */
.additional-info-section {
  display: grid;
  gap: 25px;
}

.info-block {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.info-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 15px 0;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 8px;
}

.info-content {
  color: #4a5568;
  line-height: 1.6;
}

.no-info {
  color: #a0aec0;
  font-style: italic;
}

.flavor-description,
.presentation-text {
  margin: 0;
}

.weight-display {
  max-width: 200px;
}

.weight-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.weight-table th,
.weight-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
}

.weight-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #4a5568;
}

.total-weight {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
}

.recommendations-grid {
  display: grid;
  gap: 15px;
}

.recommendation-item {
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid #4caf50;
}

.recommendation-item strong {
  color: #2d3748;
  display: block;
  margin-bottom: 5px;
}

.recommendation-item p {
  margin: 0;
  color: #4a5568;
}

.download-section {
  text-align: center;
  padding: 20px 0;
}

.download-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.download-btn:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
}

.download-icon {
  font-size: 1.2rem;
}

/* Detail View Styles (unchanged from previous version) */
.detail-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
}

.back-btn {
  background: #718096;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.detail-section h2 {
  margin-bottom: 15px;
  color: #2d3748;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 5px;
  font-weight: 700;
}

.no-data {
  text-align: center;
  color: #a0aec0;
  padding: 20px;
  font-style: italic;
}

.unit-info {
  display: grid;
  gap: 15px;
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
}

.unit-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.unit-item label {
  font-weight: 600;
  color: #4a5568;
}

.unit-value {
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-weight: 500;
}

.attributes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.attribute-item {
  background: #f8fafc;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attribute-item label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.875rem;
}

.attribute-item span {
  background: white;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.temp-badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .recipe-header-section {
    height: 250px;
  }

  .recipe-title {
    font-size: 2rem;
  }

  .title-overlay {
    padding: 20px;
  }

  .macros-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ingredients-table,
  .preparation-table {
    font-size: 0.875rem;
  }

  .subgroup-col {
    width: 120px;
  }

  .procedure-content {
    max-width: none;
  }

  .attributes-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .recipe-header-section {
    height: 200px;
  }

  .recipe-title {
    font-size: 1.5rem;
  }

  .title-overlay {
    padding: 15px;
  }

  .macros-grid {
    grid-template-columns: 1fr;
  }

  .ingredients-table th,
  .ingredients-table td,
  .preparation-table th,
  .preparation-table td {
    padding: 8px;
    font-size: 0.8rem;
  }
}

.cook-method {
  font-size: 0.75rem;
  color: #718096;
  font-style: italic;
  margin-left: 4px;
}

.badge-cook {
  background: #ffe082;
  color: #795548;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 0.8em;
  margin-left: 4px;
}
.badge-cut {
  background: #b2ebf2;
  color: #006064;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 0.8em;
  margin-left: 4px;
}
.protein-cell {
  color: #388e3c;
  font-weight: 600;
}
.carb-cell {
  color: #1976d2;
  font-weight: 600;
}
.fat-cell {
  color: #fbc02d;
  font-weight: 600;
}

.subgroup-link {
  background: none;
  border: none;
  color: #2d3748;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  text-align: left;
  padding: 0;
  margin-bottom: 4px;
}
.subgroup-link:hover {
  color: #4caf50;
  text-decoration: underline;
}

.flavor-subgroup-block {
  margin-bottom: 8px;
}

.storage-subgroup-block {
  margin-bottom: 8px;
}

/* Estilos para las filas de totales */
.subgroup-total-row {
  background-color: #e8f5e8;
  border-top: 2px solid #4caf50;
}

.subgroup-total-label {
  text-align: right;
  padding: 12px 8px;
  font-size: 14px;
  color: #2c3e50;
}

.subgroup-total-cost {
  text-align: center;
  padding: 12px 8px;
  font-size: 14px;
  color: #2c3e50;
  background-color: #e8f5e8;
}

.recipe-total-row {
  background-color: #fff3cd;
  border-top: 3px solid #ffc107;
  border-bottom: 3px solid #ffc107;
}

.recipe-total-label {
  text-align: right;
  padding: 16px 8px;
  font-size: 16px;
  color: #856404;
}

.recipe-total-cost {
  text-align: center;
  padding: 16px 8px;
  font-size: 16px;
  color: #856404;
  background-color: #fff3cd;
}

/* Estilos para el resumen de costos */
.cost-summary {
  display: grid;
  gap: 15px;
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.cost-item label {
  font-weight: 600;
  color: #4a5568;
  font-size: 1rem;
}

.cost-item .cost-value {
  font-weight: 700;
  color: #2c3e50;
  font-size: 1.2rem;
  background: #e8f5e8;
  padding: 8px 12px;
  border-radius: 6px;
  border: 2px solid #4caf50;
}

/* Back Button Styles */
.back-button-container {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 15;
}

.back-to-list-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #2d3748;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.back-to-list-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.back-icon {
  margin-right: 8px;
  font-size: 1.1rem;
}
</style>
